import moment from 'moment-timezone';
import { MenuItem, Select } from '@mui/material';
import {
  faCalendarClock,
  faCircleInfo,
} from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode } from 'react';
import { Typography } from '@design-system/components/typography/typography';
import { ExistingScheduleTime } from '../model/scheduleGroups.domain';
import {
  clearButton,
  divider,
  dividerLine,
  existingTime,
  existingTimeField,
  existingTimeHint,
  existingTimeMenuItem,
  existingTimePlaceholder,
  joinNote,
  joinedPicker,
} from './existingTimeSelect.css';

type ExistingTimeSelectProps = {
  times: Array<ExistingScheduleTime>;
  joined: ExistingScheduleTime | undefined;
  onJoin: (time: ExistingScheduleTime) => void;
  onClear: () => void;
  timeZone: string;
  programName: string | undefined;
};

const workoutsLabel = (count: number) =>
  `${count} ${count === 1 ? 'workout' : 'workouts'}`;

const formatOption = (time: ExistingScheduleTime, timeZone: string) =>
  `${moment
    .tz(time.publishedAt, timeZone)
    .format('ddd, MMM D YYYY · h:mm A')} — ${workoutsLabel(time.count)}`;

const CaretIcon = () => <FontAwesomeIcon icon={faCaretDown} />;

export const ExistingTimeSelect = (p: ExistingTimeSelectProps) => (
  <>
    <div className={existingTime}>
      <Typography as="label" size="medium" color="N-100">
        Join an existing time
      </Typography>
      <Select<number | ''>
        value={p.joined?.key ?? ''}
        displayEmpty
        className={existingTimeField}
        data-testid="existingTimeSelect"
        startAdornment={<FontAwesomeIcon icon={faCalendarClock} />}
        IconComponent={p.joined ? () => null : CaretIcon}
        endAdornment={
          p.joined ? (
            <button
              type="button"
              aria-label="Clear existing time"
              className={clearButton}
              onClick={p.onClear}
            >
              <FontAwesomeIcon icon={faCircleXmark} />
            </button>
          ) : null
        }
        renderValue={() =>
          p.joined ? (
            formatOption(p.joined, p.timeZone)
          ) : (
            <span className={existingTimePlaceholder}>
              Choose an existing time
            </span>
          )
        }
      >
        {p.times.map((time) => (
          <MenuItem
            key={time.key}
            value={time.key}
            className={existingTimeMenuItem}
            onClick={() => p.onJoin(time)}
          >
            {formatOption(time, p.timeZone)}
          </MenuItem>
        ))}
      </Select>
      <Typography size="small" className={existingTimeHint}>
        {p.joined
          ? 'Date & time come from this group. Clear (×) to choose your own.'
          : `Times already used in ${
              p.programName || 'this program'
            }. Optional.`}
      </Typography>
    </div>
    <div className={divider}>
      <span className={dividerLine} />
      <Typography size="small" color="N-60">
        {p.joined
          ? 'Clear the existing time to pick a new date & time'
          : 'or pick a new date & time'}
      </Typography>
      <span className={dividerLine} />
    </div>
  </>
);

export const JoinedGroupNote = ({
  time,
  timeZone,
  joiningCount,
}: {
  time: ExistingScheduleTime;
  timeZone: string;
  joiningCount: number;
}) => (
  <div className={joinNote}>
    <FontAwesomeIcon icon={faCircleInfo} />
    <Typography as="span" fontWeight="normal">
      {joiningCount === 1
        ? 'This workout joins'
        : `These ${joiningCount} workouts join`}{' '}
      the {moment.tz(time.publishedAt, timeZone).format('ddd, MMM D, h:mm A')}{' '}
      group ({workoutsLabel(time.count + joiningCount)}).
    </Typography>
  </div>
);

export const LockedWhenJoined = (p: {
  joined: ExistingScheduleTime | undefined;
  children: ReactNode;
}) => <div className={p.joined ? joinedPicker : undefined}>{p.children}</div>;
